import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSwipeGesture } from './useSwipeGesture';

/**
 * Ordered list of navigable pages matching the bottom/side nav.
 */
const NAV_PAGES = ['/', '/roll', '/practice', '/settings', '/about'];

/**
 * Hook that enables left/right swipe gestures to navigate between
 * the main nav pages. Spread the returned handlers onto the
 * swipeable container element.
 *
 * @returns Merged pointer-event handlers for both swipe directions
 */
export function useSwipeNavigation() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentIndex = NAV_PAGES.indexOf(pathname);

  const goLeft = useCallback(() => {
    if (currentIndex > 0) {
      navigate(NAV_PAGES[currentIndex - 1]);
    }
  }, [currentIndex, navigate]);

  const goRight = useCallback(() => {
    if (currentIndex >= 0 && currentIndex < NAV_PAGES.length - 1) {
      navigate(NAV_PAGES[currentIndex + 1]);
    }
  }, [currentIndex, navigate]);

  // Swipe left (finger moves left) = go to next page (right in nav order)
  const left = useSwipeGesture({ direction: 'left', onSwipe: goRight });

  // Swipe right (finger moves right) = go to previous page (left in nav order)
  const right = useSwipeGesture({ direction: 'right', onSwipe: goLeft });

  /** Merge both direction handlers so a single element tracks both gestures. */
  const handlers = useMemo(
    () => ({
      onPointerDown: (e: React.PointerEvent) => {
        left.onPointerDown(e);
        right.onPointerDown(e);
      },
      onPointerUp: (e: React.PointerEvent) => {
        left.onPointerUp(e);
        right.onPointerUp(e);
      },
      onPointerCancel: () => {
        left.onPointerCancel();
        right.onPointerCancel();
      },
    }),
    [left, right]
  );

  return handlers;
}
